import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid'
import { Group, MeshBasicMaterial, Vector3 } from 'three'
import type { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { DrawEntityCommand } from '@/plugins/cad/module/CADModule/command/DrawEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import {
  AlignDimensionEntity,
  getAlignDimensionPoints,
} from '@/plugins/cad/module/CADModule/model/entity'
import { ArrowObject } from '@/plugins/cad/module/CADModule/object/entityObject/ArrowObject'
import { createCadLineSegments2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils, type ILine, type IPoint } from '@/plugins/cad/module/utils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'
import { formatLength } from '@/shared/unitPresentation'

interface IDimPointData {
  d1: Vector3
  d2: Vector3
  e1_start: Vector3
  e1_end: Vector3
  e2_start: Vector3
  e2_end: Vector3
  d: number
}

const dimensionMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryFinished: () => {},
    entryPoint2: () => {},
  },
}).createMachine({
  id: 'drawDimensionTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: 'draw',
      },
    },
    draw: {
      initial: 'align',
      states: {
        align: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.dimension.firstOrigin },
            },
            point2: {
              on: { NEXT: 'point3' },
              entry: [
                { type: 'showMouseTip', params: CadTipKey.draw.dimension.secondOrigin },
                'entryPoint2',
              ],
            },
            point3: {
              on: { NEXT: '#drawDimensionTool.finished' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.dimension.linePosition },
            },
          },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 长度标注绘制工具，依次拾取两个基准点并放置标注位置。
 */
@injectable()
export class DrawDimensionTool extends StateMachineTool<typeof dimensionMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawDimensionTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = dimensionMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
      entryPoint2: () => this.entryPoint2(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private previewLineObject: LineSegments2 | null = null
  private leftArrowObject: ArrowObject | null = null
  private rightArrowObject: ArrowObject | null = null
  private entity: AlignDimensionEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private dimPointData: IDimPointData | null = null
  private inputElement: HTMLInputElement | null = null
  private unsubscribeUnitPreference: (() => void) | null = null
  // 是否编辑过输入框
  private isInputEdited: boolean = false

  protected onInitialize(entityData?: Partial<AlignDimensionEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      undefined,
      this.isCanCommit.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.unsubscribeUnitPreference = this.unitPresentation.subscribe(() => {
      if (!this.isInputEdited) {
        this.updateInputBox()
      }
    })
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.unsubscribeUnitPreference?.()
    this.unsubscribeUnitPreference = null
    this.historyManager.commitCommand()
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.leftArrowObject && DrawUtils.disposeObject(this.leftArrowObject)
    this.rightArrowObject && DrawUtils.disposeObject(this.rightArrowObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.previewLineObject = null
    this.leftArrowObject = null
    this.rightArrowObject = null
    this.entity = null
    this.currentCommand = null
    this.dimPointData = null
    if (this.inputElement) {
      this.inputElement.parentElement?.removeChild(this.inputElement)
      this.inputElement = null
    }
    this.isInputEdited = false
  }

  /**
   * 鼠标按下：左键依次记录两个基准点与一个放置点。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint && this.points.length < 3) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.updateCanvas()
        this.send({ type: 'NEXT' })
      }
    } else if (params.event.button === 2) {
      // 右键
      this.finishDrawing()
    }
  }

  /**
   * 鼠标移动：更新当前待确认点的预览位置。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.points.length >= 3) {
      return
    }
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    } else if (params.event.keyCode === KeyCodes.ENTER) {
      this.finishDrawing()
    }
  }

  // 正交/捕捉/极轴/点云 统一处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })

    this.tempPoint = {
      x: finalPoint.x,
      y: finalPoint.y,
    }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  private entryPoint2() {
    // 显示文本输入框
    const { screenPoint, angle, length } = this.getInputBoxPositionData()
    this.inputElement = this.createInputBox(screenPoint, angle)
    if (screenPoint) {
      this.inputElement.style.visibility = 'visible'
      this.inputElement.value = length
        ? formatLength(length, this.unitPresentation.preference).valueText
        : ''
    }
  }

  private getInputBoxPositionData() {
    const points = this.tempPoint ? this.points.concat(this.tempPoint) : [...this.points]
    let p1: IPoint | undefined
    let p2: IPoint | undefined
    let midPoint: { x: number; y: number } | undefined
    let angle: number | undefined
    let length: number | undefined
    if (this.dimPointData) {
      p1 = this.dimPointData.d1
      p2 = this.dimPointData.d2
    } else if (points.length >= 2) {
      p1 = points[0]
      p2 = points[1]
    }
    if (p1 && p2) {
      midPoint = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      }
      const vector = new Vector3().subVectors(
        new Vector3(p2.x, p2.y, 0),
        new Vector3(p1.x, p1.y, 0)
      )
      const dir = vector.clone().normalize()
      angle = Math.atan2(dir.y, dir.x)
      length = vector.length()

      //  防止翻转
      if (angle > Math.PI / 2 || angle < -Math.PI / 2) {
        angle += Math.PI // 旋转 180 度
      }
    }
    return {
      screenPoint: midPoint
        ? this.controller.getScreenPosition({ x: midPoint.x, y: midPoint.y, z: 0 })
        : undefined,
      angle,
      length,
    }
  }

  private createInputBox(screenPoint: IPoint = { x: 0, y: 0 }, angle = 0): HTMLInputElement {
    const inputElement = document.createElement('input')
    Object.assign(inputElement.style, {
      position: 'absolute',
      left: `${screenPoint.x}px`,
      top: `${screenPoint.y}px`,
      width: '60px',
      maxWidth: '-webkit-fill-available',
      outline: 'none',
      backgroundColor: '#fff',
      visibility: 'hidden',
      transform: `translate(-50%, -100%) rotate(${-angle}rad)`,
      color: '#000',
    })
    this.controller.domElement.parentElement?.appendChild(inputElement)
    inputElement.focus()
    inputElement.addEventListener('input', () => {
      this.isInputEdited = true
    })
    inputElement.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault()
        this.finishDrawing()
      }
    })
    inputElement.addEventListener('contextmenu', (event) => {
      event.preventDefault()
      this.finishDrawing()
    })
    return inputElement
  }

  private finishDrawing() {
    if (this.points.length >= 3) {
      this.send({ type: 'NEXT' })
      return
    }
    if (this.points.length === 2 && this.tempPoint) {
      this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
      this.updateCanvas()
      this.send({ type: 'NEXT' })
    }
  }

  private updateInputBox() {
    if (!this.inputElement) {
      return
    }
    const { screenPoint, angle, length } = this.getInputBoxPositionData()
    if (!screenPoint) {
      this.inputElement.style.visibility = 'hidden'
      return
    }
    Object.assign(this.inputElement.style, {
      left: `${screenPoint.x}px`,
      top: `${screenPoint.y}px`,
      transform: `translate(-50%, -100%) rotate(${-(angle ?? 0)}rad)`,
    })
    this.inputElement.style.visibility = 'visible'
    this.inputElement.focus()
    if (!this.isInputEdited) {
      this.inputElement.value = length
        ? formatLength(length, this.unitPresentation.preference).valueText
        : ''
      this.inputElement.select()
    }
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
    this.updateInputBox()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }

    this.dimPointData =
      points.length > 2
        ? getAlignDimensionPoints(
            new Vector3(points[0].x, points[0].y, 0),
            new Vector3(points[1].x, points[1].y, 0),
            new Vector3(points[2].x, points[2].y, 0)
          )
        : null
    const p1 = this.dimPointData ? this.dimPointData.d1 : points[0]
    const p2 = this.dimPointData ? this.dimPointData.d2 : points[1]
    this.updatePreviewLine(points)
    this.updateArrowObject(p1, p2)
  }

  private get materialColor() {
    return this.entity ? this.entity.color : 0xffffff
  }
  private updatePreviewLine(points: IPoint[]) {
    if (points.length < 2 || !this.entity) {
      return
    }
    const lines: ILine[] = []
    if (points.length === 2) {
      lines.push({ startPoint: points[0], endPoint: points[1] })
    } else if (points.length > 2 && this.dimPointData) {
      // 大于两点时，还需绘制延伸线
      const { d1, d2, e1_start, e1_end, e2_start, e2_end } = this.dimPointData
      lines.push({ startPoint: d1, endPoint: d2 })
      lines.push({ startPoint: e1_start, endPoint: e1_end })
      lines.push({ startPoint: e2_start, endPoint: e2_end })
    }
    const geometry = DrawUtils.getLineSegmentsGeometryByLines(lines)
    if (!this.previewLineObject) {
      this.previewLineObject = createCadLineSegments2Object(
        geometry,
        MaterialUtils.createMaterialByLineType(
          this.entity.lineType,
          MaterialUtils.getMaterialOptionByEntity(this.entity)
        )
      )
      this.previewLineObject.computeLineDistances()
      this.group.add(this.previewLineObject)
      return
    }
    this.previewLineObject.geometry.dispose()
    this.previewLineObject.geometry = geometry
    this.previewLineObject.computeLineDistances()
  }

  private updateArrowObject(p1: IPoint, p2: IPoint) {
    let material: MeshBasicMaterial | undefined
    const length = 2.5
    if (!this.leftArrowObject || !this.rightArrowObject) {
      material = new MeshBasicMaterial({
        color: this.materialColor,
        depthTest: false,
        depthWrite: false,
      })
    }
    if (!this.leftArrowObject) {
      this.leftArrowObject = new ArrowObject({ length, material })
      this.leftArrowObject.name = 'leftArrow'
      this.group.add(this.leftArrowObject)
    }
    if (!this.rightArrowObject) {
      this.rightArrowObject = new ArrowObject({ length, material })
      this.rightArrowObject.name = 'rightArrow'
      this.group.add(this.rightArrowObject)
    }
    const start = new Vector3(p1.x, p1.y, 0)
    const end = new Vector3(p2.x, p2.y, 0)
    const dir = new Vector3().subVectors(end, start).normalize()
    this.leftArrowObject.update(start, dir, true)
    this.rightArrowObject.update(end, dir, false)
    const isShowArrow = start.distanceTo(end) >= 10
    this.leftArrowObject.visible = isShowArrow
    this.rightArrowObject.visible = isShowArrow
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<AlignDimensionEntity>) {
    const entity = new AlignDimensionEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    if (entityData?.textHeight) {
      entity.textHeight = entityData.textHeight
    }
    return entity
  }

  private updateEntity() {
    if (!this.entity || !this.dimPointData) {
      return
    }
    const { d1, d2 } = this.dimPointData
    const textPosition = {
      x: (d1.x + d2.x) / 2,
      y: (d1.y + d2.y) / 2,
    }
    Object.assign(this.entity, {
      p1: this.points[0],
      p2: this.points[1],
      offsetPoint: this.points[2],
      textPosition,
      dimensionText: this.inputElement && this.isInputEdited ? this.inputElement.value : '',
    })
  }

  private isCanCommit(): boolean {
    return !!this.entity?.offsetPoint
  }
}
